import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import constants from '../constants';
import { useTestState } from '../context/TestContext';
import useTracking from '../hooks/useTracking';
import { hmacEncode, sanitizePayload } from '../utils';
import { useLogError } from '../hooks/useLogError';
import useTrackGAConnector from '../hooks/useTrackGAConnector';

// 2025-mar: minimum set of required fields to ingest from app.audicus.com URL param
interface UserData {
  BD_ContactId: string;
  BD_FacilityId: string;
  BD_First_Name: string;
  BD_Last_Name: string;
  BD_Sex: string;
  BD_Dob: string;
  BD_ID: string;
  BD_First_Test: string;
  BD_Admin_Name: string;
  BD_Admin_Email: string;
  BD_Age: string;
}

interface SelectInput {
  question_title: Record<string, string>;
  response_options: Record<string, string[]>;
  api_name: string;
  data_type: string;
}

const SELECT_INPUTS: SelectInput[] = [
  {
    question_title: {
      en: 'Has the patient had any surgeries, ear infections or other procedures regarding their ear?',
      es: '¿El paciente ha tenido alguna cirugía, infección de oído u otros procedimientos relacionados con el oído?',
    },
    response_options: {
      en: ['No', 'Yes'],
      es: ['No', 'Sí'],
    },
    api_name: 'CHT_any_ear_procedures__c',
    data_type: 'boolean',
  },
  {
    question_title: {
      en: 'Does the patient have trouble understanding conversations day-to-day?',
      es: '¿El paciente tiene problemas para comprender conversaciones cotidianas?',
    },
    response_options: {
      en: ['No', 'Yes'],
      es: ['No', 'Sí'],
    },
    api_name: 'CHT_trouble_understanding_conversations__c',
    data_type: 'boolean',
  },
  {
    question_title: {
      en: 'Has the patient ever worn hearing aids before?',
      es: '¿El paciente ha usado alguna vez audífonos anteriormente?',
    },
    response_options: {
      en: ['No', 'Yes - Right Ear', 'Yes - Left Ear', 'Yes - Both Ears'],
      es: [
        'No',
        'Sí - Oído derecho',
        'Sí - Oído izquierdo',
        'Sí - Ambas orejas',
      ],
    },
    api_name: 'CHT_ever_worn_hearing_aid__c',
    data_type: 'text',
  },
  {
    question_title: {
      en: 'In the past 90 days, has the patient experienced a sudden or rapid worsening of hearing loss?',
      es: 'En los últimos 90 días, ¿el paciente ha experimentado un empeoramiento repentino o rápido de la pérdida auditiva?',
    },
    response_options: {
      en: ['No', 'Yes'],
      es: ['No', 'Sí'],
    },
    api_name: 'CHT_hearing_rapid_loss__c',
    data_type: 'boolean',
  },
  {
    question_title: {
      en: 'Does the patient experience dizziness or vertigo?',
      es: '¿El paciente experimenta mareos o vértigo?',
    },
    response_options: {
      en: ['No', 'Yes'],
      es: ['No', 'Sí'],
    },
    api_name: 'CHT_dizziness__c',
    data_type: 'boolean',
  },
];

const EarHistory = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('earHistory');
  const currentLanguage = i18n.language;
  const { register, watch, setValue, handleSubmit } = useForm();
  const { logError } = useLogError();
  const { trackEvent } = useTracking();
  const { trackGAConnectorEvent, isGAConnectorEventTracked } =
    useTrackGAConnector();
  const { state, dispatch } = useTestState();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [isPageLoadTracked, setIsPageLoadTracked] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (!isPageLoadTracked) {
      trackEvent('JHT_EARHISTORY_PAGE_LOAD');
      setIsPageLoadTracked(true);
    }
    if (!isGAConnectorEventTracked) {
      trackGAConnectorEvent();
    }
  }, [
    isGAConnectorEventTracked,
    isPageLoadTracked,
    trackEvent,
    trackGAConnectorEvent,
  ]);

  useEffect(() => {
    // set progress to wherever ear history form falls in route order
    dispatch({ type: 'UPDATE_PROGRESS', payload: 20 });

    if (searchParams.get('data')) {
      // TODO: try except here
      const userData: UserData = JSON.parse(
        window.atob(searchParams.get('data') || ''),
      );

      // console.log('userData', userData)
      // console.log('dob', userData.BD_Dob)
      dispatch({
        type: 'UPDATE_EARHISTORY',
        payload: {
          contactid: userData.BD_ContactId || '0031J00001Ph36K', // use fallback id of 'Catch All' Contact in SF
          facilityid: userData.BD_FacilityId || 'a0dUd000003AhYD', // use fallback id of 'Unknown' Facility in SF
          patientfirstname:
            userData.BD_First_Name || `MISSING FIRST NAME ${userData.BD_ID}`,
          patientlastname:
            userData.BD_Last_Name || `MISSING LAST NAME ${userData.BD_ID}`,
          patientsex: userData.BD_Sex,
          patientdob: userData.BD_Dob,
          patientage: userData.BD_Age,
          patientexternalid: userData.BD_ID,
          nameonresult: `${userData.BD_First_Name} ${userData.BD_Last_Name} ${userData.BD_ID}`,
          adminemail: userData.BD_Admin_Email,
          adminname: userData.BD_Admin_Name,
        },
      });

      // Set BD_First_Test as the default for CHT_ear_medical_history_notes__c
      if (userData.BD_First_Test) {
        setValue('CHT_ear_medical_history_notes__c', userData.BD_First_Test);
      }
    }

    register('dob');
  }, [dispatch, register, searchParams, setValue]);

  const [hearingResultData, setHearingResultData] = useState<
    Record<string, string | boolean>
  >({});

  const getEnglishValue = (value: string) => {
    if (currentLanguage === 'en') return value;

    // only item with more than 2 values, static for now
    const index = SELECT_INPUTS[2].response_options.es.findIndex(
      (option) => option === value,
    );
    return SELECT_INPUTS[2].response_options.en[index];
  };

  const handleSelectionChange = (
    apiName: string,
    value: string,
    dataType: string,
  ) => {
    setHearingResultData((prevData) => {
      if (
        value === 'Yes' ||
        value === 'Sí' ||
        value.includes('Yes') ||
        value.includes('Sí')
      ) {
        return {
          ...prevData,
          [apiName]: dataType === 'boolean' ? true : getEnglishValue(value),
        };
      } else {
        const updatedData = { ...prevData };
        delete updatedData[apiName]; // Remove if not "Yes"
        return updatedData;
      }
    });
  };

  const earMedicalHistoryNotes = watch('CHT_ear_medical_history_notes__c');

  useEffect(() => {
    setHearingResultData((prevData) => ({
      ...prevData,
      CHT_ear_medical_history_notes__c: earMedicalHistoryNotes || '', // Ensure an empty string if no value is provided
    }));
  }, [earMedicalHistoryNotes]);

  const onSubmit = async () => {
    setFormSubmitted(true);

    setIsButtonDisabled(true);
    setLoading(true);

    // const activityData = formData2Activity(data)
    const contactData = { id: state.contactid };
    const facilityData = { id: state.facilityid };

    const patientData = {
      first_name__c: state.patientfirstname,
      last_name__c: state.patientlastname,
      date_of_birth__c: state.patientdob || undefined,
      sex__c: state.patientsex,
      patient_id__c: state.patientexternalid,
      facility__c: state.facilityid,
    };

    // Get the current date in YYYY-MM-DD format
    const currentDate = new Date().toISOString().split('T')[0];
    // initialize the activityData
    const data = {
      contact: contactData,
      facility__c: facilityData,
      patient__c: patientData,
      hearing_result__c: {
        ...hearingResultData,
        patient_dob__c: state.patientdob || undefined,
        age__c: state.patientage,
        patient_id__c: state.patientexternalid,
        created_at_facility__c: state.facilityid,
        name_on_result__c: state.nameonresult,
        result_date__c: currentDate,
        admin_email__c: state.adminemail,
        admin_name__c: state.adminname,
      },
      activitytypeid: 'JHT Web B2B Submit Ear History Form',
    };

    const sanitizedData = sanitizePayload(data);
    if (!sanitizedData) {
      return;
    }

    const activityData = JSON.stringify(sanitizedData);
    const signature = await hmacEncode(activityData, constants['hmacSecret']);

    try {
      const response = await fetch(constants['apiBaseUrl'], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AUD-Signature': signature,
        },
        body: activityData,
      });
      const responseData = await response.json();
      // console.log('responseData', responseData);
      setLoading(false);
      if (!responseData.patient__c.id || !responseData.hearing_result__c.id) {
        // setApiPostFailed(true);
        setIsButtonDisabled(false);
        throw new Error(
          `Failed to POST to ${constants['apiBaseUrl']} on ${data.activitytypeid} for contact id: ${contactData.id}`,
        );
      } else {
        // Handle successful patient response
        dispatch({
          type: 'UPDATE_PATIENTID',
          payload: responseData.patient__c.id,
        });
        // Handle successful HR response
        dispatch({
          type: 'UPDATE_HEARINGRESULTID',
          payload: responseData.hearing_result__c.id,
        });
      }
    } catch (error) {
      setLoading(false);
      setIsButtonDisabled(false);
      logError(error);
    }
    navigate('/soundcard');
  };

  return (
    <div className="demographics">
      <h1 className="top-title-text">{t('top-title')}</h1>

      <form className="form-content" onSubmit={handleSubmit(onSubmit)}>
        {SELECT_INPUTS.map((item) => (
          <div key={item.api_name}>
            <p>{item.question_title[currentLanguage]}</p>
            <select
              style={{ width: '100%' }}
              {...register(item.api_name, {
                onChange: (e) =>
                  handleSelectionChange(
                    item.api_name,
                    e.target.value,
                    item.data_type,
                  ),
              })}
              defaultValue={'DEFAULT'}
            >
              <option value="DEFAULT" disabled>
                {t('select-option')}
              </option>
              {item.response_options[currentLanguage].map((option, i) => (
                // option data value is always in english
                <option key={`${item.api_name}-${option}`} value={option}>
                  {item.response_options[currentLanguage][i]}
                </option>
              ))}
            </select>
          </div>
        ))}

        <div>
          <p>{t('medical-history')}</p>
          <textarea
            style={{ width: '100%' }}
            {...register('CHT_ear_medical_history_notes__c', {
              onChange: () => setFormSubmitted(false),
            })}
          />
        </div>
        <input
          type="submit"
          className="link-button"
          value={loading ? t('submitting') : t('submit')}
          disabled={isButtonDisabled}
        />
      </form>
    </div>
  );
};

export default EarHistory;
